// Formatted .xlsx export of the air-export shipment list, shared by the
// Accountant and Admin dashboards so both produce the same sheet.

import { parseNumericCell } from './exportCsv';

// Same lookup ShipmentsTable uses for its invoice columns —
// invoiceMap[awbNumber] can be an array or a single raw invoice record. The
// number and date are not columns on it: they live inside invoice_data, as its
// own column on air records and nested under `invoice` on sea ones.
const getInvoiceDetails = (invoiceMap, awbNumber) => {
  const invRecord = invoiceMap?.[awbNumber];
  const raw = invRecord && (Array.isArray(invRecord) ? invRecord[0] : invRecord);
  if (!raw) return { grandTotal: null, taxable: null, invoiceNo: '', invoiceDate: '' };
  const inv = raw.invoice || raw;
  const summary = inv.summary || inv;
  const entries = [raw.invoiceData, raw.invoice_data, inv.invoiceData, inv.invoice_data]
    .find((v) => Array.isArray(v) && v.length) || [];
  const first = entries[0] || null;
  return {
    grandTotal: summary.grand_total ?? summary.grandTotal ?? null,
    taxable: summary.taxable_amount ?? summary.taxableAmount ?? null,
    invoiceNo: first?.invoiceNumber || first?.invoice_number || '',
    invoiceDate: raw.invoiceDate ?? raw.invoice_date ?? first?.invoiceDate ?? first?.invoice_date ?? '',
  };
};

// Amounts reach us already display-formatted ("1,630.00", "₹9,13,158.75"), and a
// numeric-looking string makes Excel flag the cell with its green "number stored
// as text" triangle. Hand Excel a real number instead, and let the column's
// numFmt do the formatting.
//
// Multi-value fields (e.g. multi-container shipments) come back as arrays — a
// single entry still becomes a number, several stay joined text.
const normalizeNumeric = (value) => {
  if (Array.isArray(value)) {
    if (value.length !== 1) return value.length ? value.join(' & ') : null;
    return normalizeNumeric(value[0]);
  }
  if (value === null || value === undefined || value === '') return null;
  const num = parseNumericCell(value);
  return num === null ? String(value) : num;
};

// AWB / S/B numbers are identifiers, but Excel still flags them when they arrive
// as digit-only text. Write those as plain integers and leave anything else —
// codes with letters, or a value whose leading zero carries meaning — as text.
const asIdentifier = (value) => {
  if (Array.isArray(value)) return value.length === 1 ? asIdentifier(value[0]) : value.join(' & ');
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') return value;
  const str = String(value).trim();
  const num = parseNumericCell(str);
  return num !== null && Number.isInteger(num) ? num : str;
};

// SB Date already arrives as dd/mm/yyyy; the exporter's invoice date comes
// through raw, so match it rather than mixing two formats in one sheet.
const asDayFirst = (value) => {
  if (!value) return '';
  return String(value)
    .split(/\s*&\s*/)
    .map((part) => {
      const m = part.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? `${m[3]}/${m[2]}/${m[1]}` : part.trim();
    })
    .filter(Boolean)
    .join(' & ');
};

export const exportShipmentsExcel = async (shipments = [], invoiceMap = {}) => {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Shipments');

  const moneyFmt = '#,##0.00';
  const center = { vertical: 'middle', horizontal: 'center' };
  sheet.columns = [
    { header: 'AWB Number', key: 'awbNumber', width: 16, style: { numFmt: '0', alignment: center } },
    { header: 'Username', key: 'username', width: 14, style: { alignment: center } },
    { header: 'Customer Name', key: 'customerName', width: 22, style: { alignment: center } },
    { header: 'Buyer Name', key: 'buyerName', width: 22, style: { alignment: center } },
    { header: 'SB Number', key: 'sbNumber', width: 16, style: { numFmt: '0', alignment: center } },
    { header: 'SB Date', key: 'sbDate', width: 12, style: { alignment: center } },
    // The exporter's own invoice — the invoice PDF prints these as
    // "Exp Inv No" and "Date".
    { header: 'Exp Inv No', key: 'expInvNo', width: 18, style: { alignment: center } },
    { header: 'Exp Inv Date', key: 'expInvDate', width: 14, style: { alignment: center } },
    { header: 'City Code', key: 'cityCode', width: 10, style: { alignment: center } },
    { header: 'Dest City', key: 'destCity', width: 16, style: { alignment: center } },
    { header: 'Dest Country', key: 'destCountry', width: 16, style: { alignment: center } },
    { header: 'Flight No', key: 'flightNo', width: 12, style: { alignment: center } },
    { header: 'Flight Date', key: 'flightDate', width: 14, style: { alignment: center } },
    { header: 'Total Pkt', key: 'totalPkt', width: 10, style: { numFmt: '#,##0', alignment: center } },
    { header: 'Weight (KG)', key: 'weight', width: 12, style: { numFmt: moneyFmt, alignment: center } },
    { header: 'CH Weight (KG)', key: 'chWeight', width: 14, style: { numFmt: moneyFmt, alignment: center } },
    { header: 'Amount (₹)', key: 'amount', width: 14, style: { numFmt: moneyFmt, alignment: center } },
    { header: 'Activity', key: 'activity', width: 12, style: { alignment: center } },
    { header: 'FOB (₹)', key: 'fob', width: 14, style: { numFmt: moneyFmt, alignment: center } },
    { header: 'DBK (₹)', key: 'dbk', width: 12, style: { numFmt: moneyFmt, alignment: center } },
    { header: 'Rodtep (₹)', key: 'rodtep', width: 12, style: { numFmt: moneyFmt, alignment: center } },
    { header: 'Rosctl (₹)', key: 'rosctl', width: 12, style: { numFmt: moneyFmt, alignment: center } },
    { header: 'IGST (₹)', key: 'igst', width: 12, style: { numFmt: moneyFmt, alignment: center } },
    // Not rupees: the figure comes through as the invoice's own currency.
    { header: 'FC Value ($)', key: 'fcValue', width: 14, style: { numFmt: moneyFmt, alignment: center } },
    { header: 'FC Type', key: 'fcType', width: 10, style: { alignment: center } },
    { header: 'FC Rate', key: 'fcRate', width: 10, style: { numFmt: '#,##0.0000', alignment: center } },
    { header: 'Invoice Date', key: 'invoiceDate', width: 14, style: { alignment: center } },
    { header: 'Invoice No', key: 'invoiceNo', width: 22, style: { alignment: center } },
    { header: 'Total Invoice Value (₹)', key: 'totalInvoiceValue', width: 20, style: { numFmt: moneyFmt, alignment: center } },
    { header: 'Taxable Value (₹)', key: 'taxableValue', width: 18, style: { numFmt: moneyFmt, alignment: center } },
    { header: 'AWB', key: 'awbDoc', width: 8, style: { alignment: center } },
    { header: 'LEO', key: 'leoDoc', width: 8, style: { alignment: center } },
    { header: 'INV/PL', key: 'invplDoc', width: 8, style: { alignment: center } },
    { header: 'INVOICE', key: 'invoiceDoc', width: 10, style: { alignment: center } },
    { header: 'PUR', key: 'purDoc', width: 8, style: { alignment: center } },
  ];

  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A5F' } };
  headerRow.alignment = center;
  headerRow.height = 22;
  sheet.views = [{ state: 'frozen', ySplit: 1 }];

  shipments.forEach((s) => {
    const { grandTotal, taxable, invoiceNo, invoiceDate } = getInvoiceDetails(invoiceMap, s.awbNumber);
    sheet.addRow({
      awbNumber: asIdentifier(s.awbNumber),
      username: s.username || '',
      customerName: s.customerName || '',
      buyerName: s.buyerName || '',
      sbNumber: asIdentifier(s.sbNumber),
      sbDate: s.sbDate || '',
      expInvNo: s.invoiceNumber || s.exportInvNo || '',
      expInvDate: asDayFirst(s.invoiceDate || s.exportDate || ''),
      cityCode: s.cityCode || '',
      destCity: s.destCity || '',
      destCountry: s.destCountry || '',
      flightNo: s.flightNo || '',
      flightDate: s.flightDate || '',
      totalPkt: normalizeNumeric(s.totalPkt),
      weight: normalizeNumeric(s.weight),
      chWeight: normalizeNumeric(s.chWeight),
      amount: normalizeNumeric(s.amount),
      activity: s.activity || '',
      fob: normalizeNumeric(s.fob),
      dbk: normalizeNumeric(s.dbk),
      rodtep: normalizeNumeric(s.rodtep),
      rosctl: normalizeNumeric(s.rosctl),
      igst: normalizeNumeric(s.igst),
      fcValue: normalizeNumeric(s.fcValue),
      fcType: s.fcType || '',
      fcRate: normalizeNumeric(s.fcRate),
      invoiceDate: asDayFirst(invoiceDate),
      invoiceNo: invoiceNo || '',
      totalInvoiceValue: normalizeNumeric(grandTotal),
      taxableValue: normalizeNumeric(taxable),
      awbDoc: s.awb ? 'Yes' : 'No',
      leoDoc: s.leo ? 'Yes' : 'No',
      invplDoc: s.invpl ? 'Yes' : 'No',
      invoiceDoc: s.invoice ? 'Yes' : 'No',
      purDoc: s.pur ? 'Yes' : 'No',
    });
  });

  /* The same footer the table on screen carries, so a figure checked there can
     be checked here. Only the columns that are amounts are added up; a rate is
     not a thing to total, and nor is a currency code. */
  if (shipments.length) {
    const totalOf = (key) => sheet.getColumn(key).values
      .slice(2)                                  // 0 is unused, 1 is the header
      .reduce((acc, v) => acc + (typeof v === 'number' ? v : 0), 0);

    const totals = sheet.addRow({
      awbNumber: `Total — ${shipments.length} shipment${shipments.length > 1 ? 's' : ''}`,
      totalPkt: totalOf('totalPkt'),
      weight: totalOf('weight'),
      chWeight: totalOf('chWeight'),
      amount: totalOf('amount'),
      fob: totalOf('fob'),
      dbk: totalOf('dbk'),
      rodtep: totalOf('rodtep'),
      rosctl: totalOf('rosctl'),
      igst: totalOf('igst'),
      fcValue: totalOf('fcValue'),
      totalInvoiceValue: totalOf('totalInvoiceValue'),
      taxableValue: totalOf('taxableValue'),
    });
    totals.font = { bold: true };
    totals.eachCell({ includeEmpty: true }, (c) => {
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EEF6' } };
      c.border = { top: { style: 'double', color: { argb: 'FF1E3A5F' } } };
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `shipments_${new Date().toISOString().split('T')[0]}.xlsx`;
  link.click();
  URL.revokeObjectURL(link.href);
};

export default exportShipmentsExcel;
